
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { BookOpen, Bell, ArrowRight } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useGamification } from "@/hooks/useGamification";
import { useSpacedRepetitionScheduler } from "@/hooks/useSpacedRepetitionScheduler";
import type { Question } from "@/types/quiz";

interface GenerateFlashcardsFromMistakesProps {
  questions: Question[];
  answers: (number | null)[];
  subject: string;
  category: string;
}

const GenerateFlashcardsFromMistakes: React.FC<GenerateFlashcardsFromMistakesProps> = ({ 
  questions, 
  answers, 
  subject, 
  category 
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const navigate = useNavigate();
  const { markFlashcardProgress } = useGamification();
  const { getOrCreateMistakeDeck } = useSpacedRepetitionScheduler();

  // Find mistaken questions
  const mistakeQuestions = questions.filter((question, index) => 
    answers[index] !== null && answers[index] !== question.correctanswer
  );
  
  const getAIExplanation = async (question: Question, userAnswerIndex: number | null) => {
    try {
      console.log('Fetching AI explanation for question:', question.question);
      
      const { data, error } = await supabase.functions.invoke('ai-tutoring', {
        body: {
          action: 'explain',
          data: {
            question: question.question,
            options: question.options,
            correctAnswer: question.options[question.correctanswer],
            userAnswer: userAnswerIndex !== null ? question.options[userAnswerIndex] : "No answer",
            subject: subject
          }
        }
      });

      if (error) throw error;
      console.log('Received AI explanation:', data.explanation?.substring(0, 50) + '...');
      return data.explanation || "No explanation available.";
    } catch (error) {
      console.error('Error getting AI explanation:', error);
      return "Unable to generate explanation.";
    }
  };

  const handleGenerateFlashcards = async () => {
    if (mistakeQuestions.length === 0) {
      toast.info("No mistakes found to generate flashcards from.");
      return;
    }

    try {
      setIsGenerating(true);
      const mistakeCount = mistakeQuestions.length;
      toast.info(`Creating ${mistakeCount} flashcards from your mistakes...`);
      
      // Create or get a deck for quiz mistakes with a consistent name format
      console.log('Creating or getting mistake deck for subject:', subject);
      const mistakeDeck = await getOrCreateMistakeDeck(subject, category);
      
      if (!mistakeDeck || !mistakeDeck.id) {
        toast.error('Failed to create or get mistake deck');
        throw new Error('Failed to create or get mistake deck');
      }
      
      const deckId = mistakeDeck.id;
      console.log('Using deck with ID:', deckId);
      
      // Check for existing cards to avoid duplicates
      const { data: existingCards, error: fetchError } = await supabase
        .from('flashcards')
        .select('front')
        .eq('deck_id', deckId);
      
      if (fetchError) {
        console.error('Error fetching existing cards:', fetchError);
        throw fetchError;
      }
        
      const existingQuestions = existingCards?.map(card => card.front) || [];
      console.log(`Found ${existingQuestions.length} existing flashcards in deck`);
      
      // Filter out questions that already exist as flashcards
      const newMistakeQuestions = mistakeQuestions.filter(
        q => !existingQuestions.includes(q.question)
      );
      
      if (newMistakeQuestions.length === 0) {
        toast.info("All mistake flashcards already exist in your deck!");
        setTimeout(() => {
          navigate(`/flashcards?fromQuiz=true&deckId=${deckId}&showMistakes=true`);
        }, 1500);
        return;
      }
      
      console.log(`Creating ${newMistakeQuestions.length} new flashcards`);
      
      // Generate flashcards for each mistaken question
      const flashcardsToInsert = [];
      
      // Process each mistaken question sequentially to get AI explanations
      for (const question of newMistakeQuestions) {
        const userAnswer = answers[questions.indexOf(question)];
        const userAnswerText = userAnswer !== null ? question.options[userAnswer] : "No answer";
        
        // Get AI explanation for this question
        const aiExplanation = await getAIExplanation(question, userAnswer);
        
        const now = new Date();
        const nextReviewDate = new Date();
        nextReviewDate.setDate(now.getDate() + 1); // Initial review after 1 day
        
        flashcardsToInsert.push({
          front: question.question,
          back: `Correct: ${question.options[question.correctanswer]}\n\nYour answer: ${userAnswerText}\n\n${aiExplanation}`,
          category,
          subject,
          deck_id: deckId,
          difficulty: 'medium',
          repetition_count: 0,
          confidence: 0,
          source: 'quiz_mistake', // Explicitly tag source as quiz_mistake
          last_reviewed: null,
          next_review: nextReviewDate.toISOString() // Schedule for review tomorrow
        });
      }
      
      if (flashcardsToInsert.length > 0) {
        console.log('Inserting flashcards with source=quiz_mistake:', flashcardsToInsert.length);
        const { error: insertError, data: insertedCards } = await supabase
          .from('flashcards')
          .insert(flashcardsToInsert)
          .select();
          
        if (insertError) {
          console.error('Error inserting flashcards:', insertError);
          throw insertError;
        }
        
        console.log('Successfully inserted flashcards:', insertedCards?.length || 0);
        
        // Update deck card count
        const totalCardCount = (existingCards?.length || 0) + flashcardsToInsert.length;
        
        const { error: updateDeckError } = await supabase
          .from('flashcard_decks')
          .update({ 
            card_count: totalCardCount,
            updated_at: new Date().toISOString()
          })
          .eq('id', deckId);
          
        if (updateDeckError) {
          console.error('Error updating deck card count:', updateDeckError);
          // Non-critical error, continue
        }
        
        try {
          // Trigger gamification reward
          console.log(`Marking flashcard progress with ${flashcardsToInsert.length} cards`);
          await markFlashcardProgress(flashcardsToInsert.length);
        } catch (gamificationError) {
          console.error('Error updating gamification progress:', gamificationError);
          // Continue execution even if gamification update fails
        }
          
        toast.success(`Created ${flashcardsToInsert.length} flashcards from your quiz mistakes!`);
        
        // Navigate to the flashcard review page with explicit query parameters
        console.log(`Navigating to /flashcards?fromQuiz=true&deckId=${deckId}&showMistakes=true`);
        setTimeout(() => {
          navigate(`/flashcards?fromQuiz=true&deckId=${deckId}&showMistakes=true`);
        }, 1500);
      }
    } catch (error) {
      console.error('Error generating flashcards:', error);
      toast.error('Failed to generate flashcards');
    } finally {
      setIsGenerating(false);
    }
  };

  // Add UX enhancement - Show earn XP message
  const renderEarnXpMessage = () => {
    if (mistakeQuestions.length === 0) return null;
    
    return (
      <p className="text-xs text-indigo-600 mt-1 text-center font-medium">
        Earn XP for correcting your mistakes! 🎮
      </p>
    );
  };

  return (
    <div className="mt-6">
      {mistakeQuestions.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-amber-500" />
            <p className="text-amber-800 font-medium">
              You got {mistakeQuestions.length} question{mistakeQuestions.length > 1 ? 's' : ''} wrong. 
              <span className="font-semibold"> Want to revise now?</span>
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200"
            onClick={() => handleGenerateFlashcards()}
            disabled={isGenerating}
          >
            Revise Now <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      )}
      
      <Button
        onClick={handleGenerateFlashcards}
        disabled={isGenerating || mistakeQuestions.length === 0}
        className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
      >
        <BookOpen className="mr-2 h-4 w-4" />
        {isGenerating 
          ? "Creating Flashcards..." 
          : mistakeQuestions.length === 0 
            ? "No Mistakes to Convert to Flashcards" 
            : `Create ${mistakeQuestions.length} Flashcards from Mistakes`}
      </Button>
      {mistakeQuestions.length > 0 && (
        <>
          {renderEarnXpMessage()}
          <p className="text-xs text-gray-500 mt-1 text-center">
            Flashcards will be scheduled for spaced revision
          </p>
        </>
      )}
    </div>
  );
};

export default GenerateFlashcardsFromMistakes;
